import { useEffect, useRef, useState, type MutableRefObject } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Label } from '@/custolink-newdesign/ui/label'
import { Input } from '@/custolink-newdesign/ui/input'
import { Textarea } from '@/custolink-newdesign/ui/textarea'
import { Button } from '@/custolink-newdesign/ui/button'
import { AdmSupportVideoCallSessionApi } from '@/custolink-transpiled_by_server/bc.asset_interaction.api_methods'
import type { AdmSupportVideoCallSessionApi$SessionNote } from '@/custolink-transpiled_by_server/bc.asset_interaction.api_methods'
import { useDebounceValue } from '@/custolink-utils/useDebounceValue'

type Draft = { title: string; body: string }

/** Sends the latest unsaved draft, after any save already in flight. Resolves once it is stored. */
export type SessionNotesFlush = () => Promise<void>

/**
 * Private session notes editor (D-06): a session title and free-text body, with debounced
 * single-flight autosave and a visible status line. Worker-only - this panel is only ever
 * instantiated by RemoteSupportRoomPage's sidePanel slot (an authenticated org user's own room),
 * never on a guest surface (T-04-44).
 *
 * Editing is disabled until the stored notes have loaded, so a failed or slow load can never
 * autosave an empty draft over real notes (T-04-45). At most one saveNotes request is ever in
 * flight; a draft that changed while a request was in flight is sent next, once that request
 * settles (T-04-46).
 *
 * Nothing typed is dropped when the panel or the window goes away: `flushRef` hands the caller a
 * flush it awaits before window.close(), unmounting flushes the latest dirty draft (after any save
 * still in flight, since the backend is last-write-wins), and closing the window while notes are
 * unsaved asks for confirmation first. The notes query stays cached and is seeded with every
 * save and with the draft on unmount, so reopening the narrow-layout Sheet never reloads text
 * older than what was typed.
 */
export function SessionNotesPanel(p: {
  orgId: number
  callId: number
  flushRef?: MutableRefObject<SessionNotesFlush | null>
}) {
  const queryClient = useQueryClient()
  const notesQueryKey = [
    'AdmSupportVideoCallSessionApi',
    AdmSupportVideoCallSessionApi.getNotes.name,
    p.orgId,
    p.callId,
  ]
  const notes = useQuery({
    queryKey: notesQueryKey,
    queryFn: () => AdmSupportVideoCallSessionApi.getNotes(p.orgId, p.callId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

  // Local draft, initialised once from the loaded note. Stays null (inputs disabled, nothing can
  // ever be saved) until the load succeeds.
  const [draft, setDraft] = useState<Draft | null>(null)
  const [savedKey, setSavedKey] = useState<string | null>(null)
  const [hasSavedOnce, setHasSavedOnce] = useState(false)
  // Key of the draft whose save last failed. Autosave never resends that exact draft on its own,
  // only a newer edit or an explicit Retry does, so a failing save (offline, 401, 422, 500) is
  // never retried in a tight loop.
  const [failedKey, setFailedKey] = useState<string | null>(null)

  // Mirrors of the latest draft, the last successfully stored key and the save in flight, read by
  // the flush. Refs rather than state because the flush also runs after unmount, when no further
  // render will ever update state.
  const draftRef = useRef<Draft | null>(draft)
  draftRef.current = draft
  const savedKeyRef = useRef<string | null>(null)
  const inFlightRef = useRef<Promise<unknown> | null>(null)

  useEffect(() => {
    if (notes.data != null && draft == null) {
      const initial = { title: notes.data.title, body: notes.data.body }
      setDraft(initial)
      setSavedKey(JSON.stringify(initial))
      savedKeyRef.current = JSON.stringify(initial)
    }
    // Only ever initialise once, from the first successful load - draft is intentionally not a
    // dependency here, since re-running this on every draft change would fight the user's typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes.data])

  const draftKey = draft != null ? JSON.stringify(draft) : null
  // While the draft matches the last saved/loaded key, useDebounceValue tracks it instantly (no
  // artificial "Unsaved changes" flash right after a load); once it diverges, the debounced key
  // trails 800ms behind the draft.
  const debouncedKey = useDebounceValue(draftKey, savedKey, 800)

  // Every save goes through here, so the flush can always wait for the one in flight.
  const sendSave = (d: Draft) => {
    const request = AdmSupportVideoCallSessionApi.saveNotes(p.orgId, p.callId, d).then((r) => {
      savedKeyRef.current = JSON.stringify(d)
      queryClient.setQueryData<AdmSupportVideoCallSessionApi$SessionNote>(notesQueryKey, r)
      return r
    })
    inFlightRef.current = request
    request
      .finally(() => {
        if (inFlightRef.current === request) inFlightRef.current = null
      })
      .catch(() => {
        // Reported by whoever awaits request itself; this chain only clears the in-flight marker.
      })
    return request
  }

  const flushLatest: SessionNotesFlush = async () => {
    for (;;) {
      const pending = inFlightRef.current
      if (pending == null) break
      await pending.catch(() => undefined)
    }
    const d = draftRef.current
    if (d == null || JSON.stringify(d) === savedKeyRef.current) return
    await sendSave(d)
  }
  if (p.flushRef != null) {
    p.flushRef.current = flushLatest
  }

  const save = useMutation({
    mutationFn: (d: Draft) => sendSave(d),
    onSuccess: (_r, d) => {
      setSavedKey(JSON.stringify(d))
      setHasSavedOnce(true)
      setFailedKey(null)
    },
    onError: (_e, d) => {
      setFailedKey(JSON.stringify(d))
    },
  })

  const saveMutate = save.mutate
  const savePending = save.isPending

  // Single-flight: fires when the debounced key differs from what is already saved, but never
  // while a save is already in flight - the moment that one settles, this effect re-runs and, if
  // the draft moved on again in the meantime, sends the latest draft next (T-04-46). After a failed
  // save it waits for the next edit or an explicit Retry rather than retrying on its own.
  useEffect(() => {
    if (draft == null) return
    if (debouncedKey == null) return
    if (debouncedKey === savedKey) return
    if (savePending) return
    if (draftKey === failedKey) return
    saveMutate(draft)
    // draftKey/debouncedKey already reflect draft's current content; draft itself is read fresh
    // via the closure captured at effect-run time, which is what we want to send.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKey, savedKey, savePending, failedKey])

  // Flush on unmount: the room unmounts when the worker ends the session, and the narrow-layout
  // Sheet unmounts the panel when it closes. The latest dirty draft is sent even if a save is still
  // in flight (after it settles), and seeded into the query cache so a remount starts from it
  // rather than from the text loaded before it.
  const unmountFlushRef = useRef(flushLatest)
  unmountFlushRef.current = flushLatest
  useEffect(() => {
    const key = notesQueryKey
    return () => {
      const d = draftRef.current
      if (d != null) {
        queryClient.setQueryData<AdmSupportVideoCallSessionApi$SessionNote>(key, (old) =>
          old == null ? old : { ...old, title: d.title, body: d.body },
        )
      }
      unmountFlushRef.current().catch(() => {
        // Best-effort: the panel is already gone, there is nowhere left to show an error.
      })
    }
    // notesQueryKey is rebuilt every render from orgId/callId, which are the real dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.orgId, p.callId, queryClient])

  // Closing the window (its own close button, a reload) while notes are unsaved asks first. The
  // room's own exits await the flush before window.close(), so they only get here if it failed.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const d = draftRef.current
      const dirty = d != null && JSON.stringify(d) !== savedKeyRef.current
      if (dirty || inFlightRef.current != null) {
        e.preventDefault()
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [])

  const retry = () => {
    setFailedKey(null)
    if (draft != null) saveMutate(draft)
  }

  if (notes.isError) {
    return (
      <div className="cl:flex cl:flex-col cl:gap-2">
        <h3 className="cl:text-sm cl:font-semibold">Session notes</h3>
        <p className="cl:text-sm cl:text-destructive">Could not load notes.</p>
        <Button type="button" variant="outline" size="sm" onClick={() => notes.refetch()}>
          Try again
        </Button>
      </div>
    )
  }

  const status = save.isError
    ? 'error'
    : save.isPending
      ? 'saving'
      : draftKey != null && draftKey === savedKey
        ? hasSavedOnce
          ? 'saved'
          : 'idle'
        : 'unsaved'

  return (
    <div className="cl:flex cl:flex-col cl:gap-4">
      <div className="cl:flex cl:flex-col cl:gap-1">
        <h3 className="cl:text-sm cl:font-semibold">Session notes</h3>
        <p className="cl:text-xs cl:text-muted-foreground">
          Only people in your organisation can see these notes.
        </p>
      </div>

      <div className="cl:flex cl:flex-col cl:gap-1.5">
        <Label htmlFor="session-notes-title">Session title</Label>
        <Input
          id="session-notes-title"
          maxLength={200}
          disabled={draft == null}
          value={draft?.title ?? ''}
          onChange={(e) => setDraft((d) => (d == null ? d : { ...d, title: e.target.value }))}
        />
        <p className="cl:text-xs cl:text-muted-foreground">
          Used to find this session in Support Hub search
        </p>
      </div>

      <div className="cl:flex cl:flex-col cl:gap-1.5">
        <Label htmlFor="session-notes-body">Notes</Label>
        <Textarea
          id="session-notes-body"
          maxLength={20000}
          disabled={draft == null}
          className="cl:min-h-40"
          value={draft?.body ?? ''}
          onChange={(e) => setDraft((d) => (d == null ? d : { ...d, body: e.target.value }))}
        />
      </div>

      <div role="status" aria-live="polite" className="cl:flex cl:items-center cl:gap-2 cl:text-xs">
        {status === 'saving' && <span className="cl:text-muted-foreground">Saving...</span>}
        {status === 'saved' && <span className="cl:text-muted-foreground">Saved</span>}
        {status === 'unsaved' && <span className="cl:text-muted-foreground">Unsaved changes</span>}
        {status === 'error' && (
          <>
            <span className="cl:text-destructive">Could not save</span>
            <Button type="button" variant="outline" size="xs" onClick={retry}>
              Retry
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export default SessionNotesPanel
